import { useState, useEffect } from "react";
import formatLastSeen, { getShortLastSeen } from "../utils/formatLastSeen";

/**
 * Custom hook for real-time last seen status updates
 * Automatically updates the status text based on how recent the last seen time is
 *
 * @param {string|Date} lastSeenDate - The last seen timestamp
 * @param {boolean} isOnline - Whether the user is currently online
 * @param {boolean} shortFormat - Use short format (for chat lists)
 * @returns {string} The formatted last seen text
 *
 * @example
 * const lastSeenText = useLastSeen(user.last_seen, user.is_online);
 * // Returns: "active now", "active 5m ago", "active today at 3:45 PM", etc.
 *
 * @example
 * const shortLastSeen = useLastSeen(user.last_seen, user.is_online, true);
 * // Returns: "now", "5m", "3h", "yesterday", etc.
 */
const useLastSeen = (lastSeenDate, isOnline = false, shortFormat = false) => {
  const [lastSeenText, setLastSeenText] = useState(() => {
    if (shortFormat) {
      return getShortLastSeen(lastSeenDate, isOnline);
    }
    return formatLastSeen(lastSeenDate, isOnline);
  });

  useEffect(() => {
    // Update immediately when dependencies change
    const updateText = () => {
      if (shortFormat) {
        setLastSeenText(getShortLastSeen(lastSeenDate, isOnline));
      } else {
        setLastSeenText(formatLastSeen(lastSeenDate, isOnline));
      }
    };

    updateText();

    // Don't set up interval if user is online or no last seen date
    if (isOnline || !lastSeenDate) {
      return;
    }

    // Calculate appropriate update interval
    const getUpdateInterval = () => {
      const now = new Date();
      const lastSeen = new Date(lastSeenDate);

      // Check if date is valid
      if (isNaN(lastSeen.getTime())) {
        return null;
      }

      const diffMs = now - lastSeen;
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);

      // Very recent (< 1 minute) - update every 5 seconds
      if (diffMinutes < 1) {
        return 5000;
      }

      // Recent (< 1 hour) - update every 30 seconds
      if (diffMinutes < 60) {
        return 30000;
      }

      // Within day (< 24 hours) - update every minute
      if (diffHours < 24) {
        return 60000;
      }

      // Older - update every 5 minutes
      return 300000;
    };

    const interval = getUpdateInterval();

    // Don't set up interval if date is invalid
    if (interval === null) {
      return;
    }

    // Set up periodic updates
    const intervalId = setInterval(updateText, interval);

    // Cleanup
    return () => {
      clearInterval(intervalId);
    };
  }, [lastSeenDate, isOnline, shortFormat]);

  return lastSeenText;
};

export default useLastSeen;
